import { useEffect, useState, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { Navbar } from "@/components/Navbar";
import { AuthImage } from "@/components/AuthImage";
import { TodayWidget } from "@/components/TodayWidget";
import { ShopOnboardingChecklist } from "@/components/ShopOnboardingChecklist";
import ShopApplication from "@/pages/ShopApplication";
import { PullToRefresh } from "@/components/PullToRefresh";
import { SupportChat } from "@/components/SupportChat";
import { MapPin, Lock, Crown, Gift, Sparkles, Zap, X, ClipboardList, AlertCircle, Clock, TrendingUp, Eye, Share2, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { tierMeta } from "@/lib/tiers";

function TimeUntil({ isoDate }) {
  const [text, setText] = useState("");
  useEffect(() => {
    if (!isoDate) return;
    const update = () => {
      const diff = new Date(isoDate).getTime() - Date.now();
      if (diff <= 0) { setText("now"); return; }
      const hrs = Math.floor(diff / 3_600_000);
      const mins = Math.floor((diff % 3_600_000) / 60_000);
      setText(hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`);
    };
    update();
    const id = setInterval(update, 60_000);
    return () => clearInterval(id);
  }, [isoDate]);
  return <span>{text}</span>;
}

function SlotResetCountdown({ isoDate }) {
  const [text, setText] = useState("");
  useEffect(() => {
    if (!isoDate) return;
    const update = () => {
      const diff = new Date(isoDate).getTime() - Date.now();
      if (diff <= 0) { setText("Resetting now"); return; }
      const days = Math.floor(diff / 86_400_000);
      const hrs = Math.floor((diff % 86_400_000) / 3_600_000);
      if (days > 0) {
        setText(`Resets in ${days}d ${hrs}h`);
      } else {
        const mins = Math.floor((diff % 3_600_000) / 60_000);
        setText(hrs > 0 ? `Resets in ${hrs}h ${mins}m` : `Resets in ${mins}m`);
      }
    };
    update();
    const id = setInterval(update, 60_000);
    return () => clearInterval(id);
  }, [isoDate]);
  if (!text) return null;
  return <span>{text}</span>;
}

const MOCK_LEADS = [
  {
    issue_id: "demo-1",
    vehicle_year: 2019,
    vehicle_make: "Honda",
    vehicle_model: "Civic",
    description: "Front brakes are grinding when I stop. Need immediate inspection.",
    category: "Brakes/Suspension",
    created_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    expires_at: new Date(Date.now() + 22 * 60 * 60 * 1000).toISOString(),
    status: "open",
    urgent: true,
    distance_miles: 1.4,
  },
  {
    issue_id: "demo-2",
    vehicle_year: 2021,
    vehicle_make: "Toyota",
    vehicle_model: "Camry",
    description: "CEL came on yesterday. Car feels sluggish at highway speeds.",
    category: "Engine/Diagnostics",
    created_at: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
    expires_at: new Date(Date.now() + 19 * 60 * 60 * 1000).toISOString(),
    status: "open",
    urgent: false,
    distance_miles: 3.2,
  },
  {
    issue_id: "demo-3",
    vehicle_year: 2020,
    vehicle_make: "Ford",
    vehicle_model: "F-150",
    description: "Due for service at 5k miles. Prefer mobile mechanic.",
    category: "Maintenance",
    created_at: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    expires_at: new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString(),
    status: "open",
    urgent: false,
    distance_miles: 5.7,
  },
];

export default function ShopDashboard() {
  const { user, stopDemoMode } = useAuth();
  const { referralsEnabled } = useFeatureFlags();
  const navigate = useNavigate();
  const location = useLocation();
  const browseMode = location.pathname.startsWith("/browse");
  const [openIssues, setOpenIssues] = useState([]);
  const [tierStatus, setTierStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unreadChat, setUnreadChat] = useState({});
  const demoMode = !!(user?.is_demo) || (typeof localStorage !== "undefined" && localStorage.getItem("demo_mode") === "1");

  const loadIssues = useCallback(async () => {
    try {
      const a = await api.get("/issues");
      if (a.data && a.data.issues && a.data.tier_status) {
        setOpenIssues(a.data.issues.length > 0 ? a.data.issues : (demoMode ? MOCK_LEADS : []));
        setTierStatus(a.data.tier_status);
      } else if (Array.isArray(a.data)) {
        setOpenIssues(a.data.length > 0 ? a.data : (demoMode ? MOCK_LEADS : []));
        setTierStatus(null);
      } else {
        setOpenIssues(a.data);
      }
    } catch {
      // In demo mode, show mock leads even if API fails
      if (demoMode) setOpenIssues(MOCK_LEADS);
    }
  }, [demoMode]);

  const loadUnreadChat = async (issueList) => {
    try {
      const ids = issueList.filter(i => !i.locked).map(i => i.issue_id).join(",");
      if (!ids) return;
      const res = await api.get(`/issues/chat/bulk-unread?issue_ids=${ids}`);
      setUnreadChat(res.data.unread || {});
    } catch { /* silent */ }
  };

  useEffect(() => {
    (async () => {
      try {
        await loadIssues();
      } finally { setLoading(false); }
    })();
  }, [loadIssues]);

  useEffect(() => {
    if (openIssues.length > 0 && !demoMode) loadUnreadChat(openIssues);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openIssues]);

  const noLocation = user && !user.location?.lat;
  const tier = user?.subscription_tier || "free";
  const isPaid = tier === "pro" || tier === "premium";
  const isPending = user?.status === "pending";
  const applicationNotStarted = isPending && !user?.application_submitted_at;
  const applicationUnderReview = isPending && !!user?.application_submitted_at;

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <Navbar />
      
      <PullToRefresh onRefresh={loadIssues}>
        <main className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-10">
          {/* Demo Banner */}
          {demoMode && (
            <div className="mb-8 p-4 bg-[#FFB800]/10 border border-[#FFB800]/30 flex items-center justify-between gap-4 flex-wrap gap-y-3">
              <div className="flex items-center gap-3">
                <Sparkles className="text-[#FFB800] w-5 h-5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-bold text-[#FFB800] uppercase tracking-widest">Viewing Sandbox Environment</p>
                  <p className="text-xs text-zinc-400 mt-0.5">Simulated shop view for demo purposes only.</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={async () => {
                    try {
                      await api.post("/admin/demo/trigger-lead", {
                        lat: user?.location?.lat ?? null,
                        lng: user?.location?.lng ?? null,
                      });
                      toast.success("Demo lead triggered! Check the leads section.");
                      await loadIssues();
                    } catch {
                      toast.error("Could not trigger demo lead.");
                    }
                  }}
                  className="text-xs font-bold text-[#FFB800] border border-[#FFB800]/40 px-3 py-1.5 hover:bg-[#FFB800]/10 transition-colors uppercase tracking-widest"
                >
                  + Trigger Lead
                </button>
                <button onClick={stopDemoMode} className="text-xs text-zinc-400 hover:underline underline-offset-4">Exit Preview</button>
              </div>
            </div>
          )}

          {/* Location Nudge Banner */}
          {!demoMode && user?.status === "approved" && (!user?.location?.lat || !user?.location?.lng) && (
            <div className="mb-8 p-4 bg-[#FFB800]/10 border border-[#FFB800]/30 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <MapPin className="text-[#FFB800] w-5 h-5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-bold text-[#FFB800] uppercase tracking-widest">Set Your Shop Location</p>
                  <p className="text-xs text-zinc-400 mt-0.5">You won't receive leads until your shop is pinned on the map.</p>
                </div>
              </div>
              <Link to="/settings?tab=location" className="text-xs font-bold text-[#FFB800] border border-[#FFB800]/40 px-3 py-1.5 hover:bg-[#FFB800]/10 transition-colors whitespace-nowrap uppercase tracking-widest">
                Pin Location →
              </Link>
            </div>
          )}

          {/* Founding Shop Banner — Fix 4 */}
          {!demoMode && user?.status === "approved" && (user?.founding_days_remaining > 0 || user?.is_founding_member) && (
            <div className="mb-8 p-5 bg-gradient-to-r from-emerald-500/10 to-emerald-500/5 border border-emerald-500/20 rounded-lg">
              <div className="flex items-start gap-3">
                <Gift className="text-emerald-400 w-6 h-6 flex-shrink-0 mt-0.5" />
                <div className="flex-1 space-y-2">
                  <h3 className="text-base font-bold text-emerald-300">🎉 You're a Founding Shop</h3>
                  <p className="text-sm font-semibold text-emerald-200">
                    Your gift: 120 days of FREE Premium access
                  </p>
                  <p className="text-sm text-zinc-400 leading-relaxed">
                    Your ShiftQuote account is always free to use. As a thank-you for being one of our founding shops, we're giving you full Premium features — at no charge — for 120 days while we build your local driver network. No credit card. No catch.
                  </p>
                  <div className="flex items-center gap-4 flex-wrap mt-3">
                    <span className="text-xs font-mono font-bold text-emerald-400 bg-emerald-500/10 px-3 py-1.5 rounded border border-emerald-500/20">
                      ⏳ {user.founding_days_remaining} days of Premium access remaining
                    </span>
                  </div>
                  {referralsEnabled && user?.referral_code && (
                    <div className="mt-3 flex items-center gap-2 text-xs text-zinc-500">
                      <Share2 className="w-3.5 h-3.5" />
                      <span>Know a shop owner? Share your referral link:</span>
                      <button
                        onClick={() => {
                          const link = `https://www.shiftquote.app?ref=${user.referral_code}`;
                          navigator.clipboard?.writeText(link);
                          toast.success("Referral link copied!");
                        }}
                        className="text-emerald-400 font-mono underline underline-offset-2 hover:text-emerald-300 transition-colors"
                      >
                        shiftquote.app?ref={user.referral_code}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Application Status Banner */}
          {user && user.status !== "approved" && (
            <>
              {/* shop-status-access-nov2: pending = preview ("taster") mode */}
              {isPending && (
                <div className="mb-4 p-4 bg-[#FFB800]/10 border border-[#FFB800]/30 rounded-lg flex items-start gap-3" data-testid="pending-approval-banner">
                  <Clock className="text-[#FFB800] w-5 h-5 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-bold text-[#FFB800] uppercase tracking-widest">Pending approval: preview mode</p>
                    <p className="text-xs text-zinc-400 mt-0.5">You can preview nearby jobs. Quoting, chat, bookings and job alerts unlock as soon as an admin approves your shop.</p>
                  </div>
                </div>
              )}
              {/* Not started yet */}
              {applicationNotStarted && (
                <div className="mb-8 p-6 bg-gradient-to-r from-[#FF3B30]/10 to-[#FF3B30]/5 border border-[#FF3B30]/20 rounded-lg">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-3">
                      <ClipboardList className="text-[#FF3B30] w-6 h-6 flex-shrink-0" />
                      <div>
                        <h3 className="text-lg font-bold text-white">Verify Your Business</h3>
                        <p className="text-sm text-zinc-400 mt-0.5">Verify your business to start receiving leads.</p>
                      </div>
                    </div>
                    <Link to="/shop/apply" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white text-sm font-bold uppercase tracking-widest transition-colors rounded">
                      Verify Your Business
                    </Link>
                  </div>
                </div>
              )}
              {/* Submitted — under review */}
              {applicationUnderReview && (
                <div className="mb-8 p-6 bg-gradient-to-r from-[#FFB800]/10 to-[#FFB800]/5 border border-[#FFB800]/20 rounded-lg">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-3">
                      <ClipboardList className="text-[#FFB800] w-6 h-6 flex-shrink-0" />
                      <div>
                        <h3 className="text-lg font-bold text-white">Application Submitted — Under Review</h3>
                        <p className="text-sm text-zinc-400 mt-0.5">Our team is reviewing your application. We will notify you once a decision is made.</p>
                      </div>
                    </div>
                    <Link to="/shop/apply" className="inline-flex items-center gap-2 px-5 py-2.5 border border-[#FFB800]/40 text-[#FFB800] text-sm font-bold uppercase tracking-widest hover:bg-[#FFB800]/10 transition-colors rounded">
                      View Application
                    </Link>
                  </div>
                </div>
              )}
              {/* Rejected — needs changes */}
              {user.status === "rejected" && (
                <div className="mb-8 p-6 bg-gradient-to-r from-red-900/20 to-red-900/10 border border-red-500/30 rounded-lg">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-3">
                      <ClipboardList className="text-red-400 w-6 h-6 flex-shrink-0" />
                      <div>
                        <h3 className="text-lg font-bold text-white">Action Required — Application Needs Changes</h3>
                        <p className="text-sm text-zinc-400 mt-0.5">{user.rejection_reason || "Your application needs updates before it can be approved."}</p>
                      </div>
                    </div>
                    <Link to="/shop/apply" className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-500 hover:bg-red-600 text-white text-sm font-bold uppercase tracking-widest transition-colors rounded">
                      Update and Resubmit
                    </Link>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Onboarding Checklist (Internal Strike Task) */}
          {!demoMode && applicationNotStarted && (
             <div className="mb-12">
                <ShopOnboardingChecklist />
             </div>
          )}

          {/* Main Dashboard Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
            
            {/* Center: Live Leads */}
            <div className="xl:col-span-2 space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-black tracking-tight uppercase font-heading">Live Leads</h1>
                  <p className="text-zinc-500 text-sm mt-1">{openIssues.length} active leads in your area</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {openIssues.map((issue) => (
                  <Link 
                    key={issue.issue_id} 
                    to={`/issue/${issue.issue_id}`}
                    state={demoMode ? { lead: issue, isSandbox: true } : undefined}
                    className="group border border-white/5 bg-[#121214] hover:bg-white/5 transition-all relative overflow-hidden"
                  >
                    <div className="p-5">
                      {/* Accepted Quote Banner */}
                      {issue.quote_accepted && (
                        <div className="mb-4 -mx-5 -mt-5 px-5 py-4 bg-emerald-500/10 border-b border-emerald-500/20">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-bold text-emerald-400">🎉 Customer accepted your quote!</span>
                          </div>
                          <p className="text-xs text-emerald-300/80 mb-2">Please contact the customer as soon as possible via chat to set up an appointment.</p>
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/messages/${issue.issue_id}`); }}
                            className="text-xs font-bold text-emerald-400 border border-emerald-500/30 px-3 py-1.5 hover:bg-emerald-500/10 transition-colors uppercase tracking-widest flex items-center gap-1.5"
                          >
                            <MessageSquare className="w-3 h-3" /> Open Chat
                          </button>
                        </div>
                      )}
                      <div className="flex justify-between items-start mb-4">
                        <div className="space-y-1">
                          <div className={`text-lg font-bold transition-colors ${issue.locked ? "text-zinc-600" : "group-hover:text-[#FF3B30]"}`}>{issue.car_year || issue.vehicle_year} {issue.car_make || issue.vehicle_make} {issue.car_model || issue.vehicle_model}</div>
                          <div className="flex items-center gap-2 text-xs text-zinc-500 font-mono">
                            <MapPin className="w-3 h-3" />
                            {issue.distance_label && (issue.distance_mi ?? issue.distance_miles) == null
                              ? `${issue.distance_label} away`
                              : <>{issue.location_approximate ? "~" : ""}{(issue.distance_mi ?? issue.distance_miles)?.toFixed(1)} miles away</>}
                          </div>
                        </div>
                        {issue.locked ? (
                          <div className="bg-amber-500/10 text-amber-400 text-[10px] font-black px-2 py-0.5 border border-amber-500/20 uppercase tracking-widest flex items-center gap-1">
                            <Lock className="w-2.5 h-2.5" /> {issue.taster ? "Preview" : "Premium"}
                          </div>
                        ) : issue.urgent ? (
                          <div className="bg-[#FF3B30]/10 text-[#FF3B30] text-[10px] font-black px-2 py-0.5 border border-[#FF3B30]/20 uppercase tracking-widest animate-pulse">
                            Urgent
                          </div>
                        ) : null}
                        {unreadChat[issue.issue_id] && (
                          <span className="inline-flex items-center gap-1 bg-[#007AFF] text-white text-[10px] font-black px-2 py-0.5 uppercase tracking-widest">
                            <MessageSquare className="w-3 h-3" />
                            MESSAGE
                          </span>
                        )}
                      </div>
                      
                      <div className={`text-sm line-clamp-2 mb-4 h-10 ${issue.locked ? "text-zinc-600 italic" : "text-zinc-400"}`}>{issue.locked ? (issue.taster ? "Preview only. Details unlock once your shop is approved." : "Upgrade to view this lead") : issue.description}</div>
                      {!issue.locked && issue.slot_resets_at && (tier === "free" || tier === "pro") && !(user?.is_founding_member && user?.founding_days_remaining > 0) && (
                        <div className="text-xs text-emerald-400/70 mb-3 flex items-center gap-1">
                          <span>🔒</span> <SlotResetCountdown isoDate={issue.slot_resets_at} />
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between pt-4 border-t border-white/5">
                         <div className="flex items-center gap-1.5">
                           <Clock className="w-3.5 h-3.5 text-zinc-500" />
                           <span className="text-[10px] font-mono text-zinc-500 uppercase">Expires in <TimeUntil isoDate={issue.expires_at} /></span>
                         </div>
                         {issue.locked ? (
                           <div className="text-amber-400 font-black text-xs uppercase tracking-widest flex items-center gap-1.5">
                             {issue.taster ? "⏳ Quoting unlocks after approval" : "🔒 Upgrade to quote this lead"}
                           </div>
                         ) : (
                           <div className={`font-black text-xs uppercase tracking-widest flex items-center gap-1.5 ${issue.quote_accepted ? "text-emerald-400" : "text-[#FF3B30]"}`}>
                             {issue.quote_accepted ? (
                               <><MessageSquare className="w-3.5 h-3.5" /> Open Chat</>
                             ) : (
                               <>View Lead <Zap className="w-3.5 h-3.5" /></>
                             )}
                           </div>
                         )}
                      </div>
                    </div>
                  </Link>
                ))}
                
                {openIssues.length === 0 && !loading && (
                  <div className="col-span-full border-2 border-dashed border-white/5 py-20 text-center">
                    <TrendingUp className="w-8 h-8 text-zinc-700 mx-auto mb-4" />
                    <div className="text-zinc-500 font-bold uppercase tracking-widest text-sm">No leads in your area yet</div>
                    <p className="text-zinc-600 text-xs mt-2 max-w-md mx-auto">We'll notify you the moment one comes in.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar: Shop Intel */}
            <div className="space-y-6">
              {/* Subscription Card */}
              <div className="border border-white/5 bg-[#121214] rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg" style={{ background: (user?.is_founding_member && user?.founding_days_remaining > 0) ? "rgba(16,185,129,0.1)" : tier === "premium" ? "#FF3B301a" : tier === "pro" ? "#FF95001a" : "rgba(113,113,122,0.1)", border: `1px solid ${(user?.is_founding_member && user?.founding_days_remaining > 0) ? "#10b981" : tier === "premium" ? "#FF3B30" : tier === "pro" ? "#FF9500" : "#3f3f46"}` }}>
                    {(user?.is_founding_member && user?.founding_days_remaining > 0) ? (
                      <Gift className="w-5 h-5" style={{ color: "#10b981" }} />
                    ) : (
                      <Crown className="w-5 h-5" style={{ color: tier === "premium" ? "#FF3B30" : tier === "pro" ? "#FF9500" : "#71717a" }} />
                    )}
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Current Plan</div>
                    <div className="text-lg font-bold" style={{ color: (user?.is_founding_member && user?.founding_days_remaining > 0) ? "#10b981" : tier === "premium" ? "#FF3B30" : tier === "pro" ? "#FF9500" : "#a1a1aa" }}>
                      {(user?.is_founding_member && user?.founding_days_remaining > 0) ? "Founding Member" : tier === "premium" ? "Premium" : tier === "pro" ? "Pro" : "Free"}
                    </div>
                  </div>
                </div>
                {(user?.founding_days_remaining > 0 || user?.is_founding_member) ? (
                  <div className="w5full py-2.5 text-center text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded">
                    You have Premium access — {user.founding_days_remaining} days remaining as a Founding Shop.
                  </div>
                ) : (
                  <button
                    onClick={() => navigate("/upgrade")}
                    className="w-full py-2.5 text-xs font-bold uppercase tracking-widest border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 transition-colors rounded"
                  >
                    Upgrade Plan
                  </button>
                )}
              </div>

              {/* Stats Card - Redesigned */}
              <div className="border border-white/5 bg-[#121214] rounded-lg p-6">
                {/* Header */}
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-white">You're all caught up.</h3>
                  <p className="text-sm text-zinc-400 mt-2 leading-relaxed">No new leads since you last checked. We'll notify you when fresh work arrives.</p>
                </div>

                {/* Stats Grid - 2x2 on mobile/sidebar, 4-col on wide screens */}
                <div className="grid grid-cols-2 gap-3">
                  {/* Active Quotes */}
                  <div className="bg-zinc-800/60 rounded-lg p-4 border-l-[3px] border-amber-400">
                    <div className="text-2xl font-bold text-white">{user?.stats?.active_quotes || 0}</div>
                    <div className="text-xs text-zinc-400 mt-1.5 leading-tight">Active Quotes</div>
                  </div>

                  {/* In Progress */}
                  <div className="bg-zinc-800/60 rounded-lg p-4 border-l-[3px] border-blue-400">
                    <div className="text-2xl font-bold text-white">{user?.stats?.in_progress || 0}</div>
                    <div className="text-xs text-zinc-400 mt-1.5 leading-tight">In Progress</div>
                  </div>

                  {/* Unread Messages */}
                  <div className="bg-zinc-800/60 rounded-lg p-4 border-l-[3px] border-orange-400">
                    <div className="text-2xl font-bold text-white">{user?.stats?.unread_messages || 0}</div>
                    <div className="text-xs text-zinc-400 mt-1.5 leading-tight">Unread Messages</div>
                  </div>

                  {/* Completed All Time */}
                  <div className="bg-zinc-800/60 rounded-lg p-4 border-l-[3px] border-emerald-400">
                    <div className="text-2xl font-bold text-white">{user?.stats?.completed || 0}</div>
                    <div className="text-xs text-zinc-400 mt-1.5 leading-tight">Completed</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </PullToRefresh>

      <SupportChat />
    </div>
  );
}
